import { useState } from 'react';
import { toast } from 'sonner';
import { X, Warning } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import {
  requestReturn,
  recordReturn,
  RETURN_GROUND_LABEL,
  type Allocation,
  type ReturnGround,
} from '@/lib/allocationsApi';
import { getApiErrorMessage } from '@/lib/api';
import { ScrollLock } from '@/components/shared/ScrollLock';

/**
 * Return of instructions — the UI for the two-step lifecycle (rC25 / rC26 / rC27).
 *
 * The two steps are separate on purpose and the modal keeps them separate:
 *
 *   REQUEST  needs a ground and a written explanation. It does NOT take counsel off the
 *            record — the allocation moves to `return_requested` and they remain
 *            instructed. The banner says so, because a barrister who thinks they are off
 *            a case they are still on is the worst possible outcome here.
 *   RECORD   clerk only. This is where the professional client's consent or a clear
 *            explanation is evidenced, and only then does counsel come off.
 *
 * All validation is the API's. The modal collects and surfaces; it does not decide which
 * grounds are mandatory or who may record a return.
 */
interface Props {
  allocation: Allocation;
  /** 'request' for counsel/clerk asking; 'record' for the clerk completing it. */
  mode: 'request' | 'record';
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const GROUNDS = Object.keys(RETURN_GROUND_LABEL) as ReturnGround[];

export function ReturnInstructionsModal({ allocation, mode, open, onClose, onSuccess }: Props) {
  const [ground, setGround] = useState<ReturnGround | ''>('');
  const [narrative, setNarrative] = useState('');
  const [explanationGiven, setExplanationGiven] = useState(false);
  const [clerkNote, setClerkNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const barristerName = `${allocation.barrister.user.firstName} ${allocation.barrister.user.lastName}`;

  async function handleSubmit() {
    setSubmitting(true);
    try {
      if (mode === 'request') {
        await requestReturn(allocation.id, {
          returnGround: ground as ReturnGround,
          returnNarrative: narrative.trim(),
        });
        toast.success('Return requested. Counsel remains on record until a clerk records the return.');
      } else {
        await recordReturn(allocation.id, {
          explanationGiven,
          ...(clerkNote.trim() ? { clerkNote: clerkNote.trim() } : {}),
        });
        toast.success('Instructions returned. The engagement remains open for other counsel.');
      }
      onSuccess();
      onClose();
    } catch (err) {
      // The API's wording explains the rule (rC27, a missing ground). Keep it.
      toast.error(getApiErrorMessage(err, 'Could not complete the return'));
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit =
    mode === 'request' ? Boolean(ground) && narrative.trim().length > 0 : explanationGiven;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <ScrollLock />
      <div className="my-auto flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-border bg-card shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <h2 className="text-sm font-semibold text-foreground">
            {mode === 'request' ? 'Return instructions' : 'Record the return'}
          </h2>
          <button onClick={onClose} aria-label="Close" className="text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <p className="text-sm text-muted-foreground">
            {barristerName} on <span className="text-foreground">{allocation.case.caseName}</span>
          </p>

          {mode === 'request' ? (
            <>
              <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
                <Warning className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" weight="fill" />
                <span>
                  This does <strong>not</strong> take counsel off the record. They remain instructed
                  until a clerk records the return.
                </span>
              </div>

              <label className="mt-4 block text-xs font-medium text-foreground" htmlFor="return-ground">
                Ground for the return
              </label>
              <select
                id="return-ground"
                value={ground}
                onChange={(e) => setGround(e.target.value as ReturnGround)}
                className="mt-1 h-9 w-full rounded-md border border-border bg-card px-2 text-sm text-foreground focus:border-brand-600 focus:outline-none"
              >
                <option value="">Select a ground…</option>
                {GROUNDS.map((g) => (
                  <option key={g} value={g}>{RETURN_GROUND_LABEL[g]}</option>
                ))}
              </select>

              <div className="mt-3">
                <ExpandableTextarea
                  label="Explanation"
                  value={narrative}
                  onChange={setNarrative}
                  placeholder="The reasons to be given to the professional client (required — rC27)"
                />
              </div>
            </>
          ) : (
            <>
              <div className="mt-3 rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
                <div className="font-medium text-foreground">Requested ground</div>
                <div className="text-muted-foreground">
                  {allocation.returnGround
                    ? RETURN_GROUND_LABEL[allocation.returnGround as ReturnGround] ?? allocation.returnGround
                    : '—'}
                </div>
                {allocation.returnNarrative && (
                  <p className="mt-1.5 text-muted-foreground">{allocation.returnNarrative}</p>
                )}
              </div>

              <label className="mt-4 flex items-start gap-2 text-sm text-foreground">
                <input
                  type="checkbox"
                  checked={explanationGiven}
                  onChange={(e) => setExplanationGiven(e.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  The instructing solicitor has consented, or has been given a clear explanation
                  of the reasons for the return.
                  <span className="block text-xs text-muted-foreground">
                    Required before counsel can come off the record (rC27).
                  </span>
                </span>
              </label>

              <div className="mt-3">
                <ExpandableTextarea
                  label="Note"
                  value={clerkNote}
                  onChange={setClerkNote}
                  placeholder="How and when the solicitor was informed (optional but recommended)"
                />
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || submitting} loading={submitting}>
            {mode === 'request' ? 'Request return' : 'Record return'}
          </Button>
        </div>
      </div>
    </div>
  );
}
